/**
 * Authoritative Residual Risk Matrix
 * 
 * Single source of truth for residual risk calculation.
 * - Row = inherent risk (priority_weight)
 * - Column = control effectiveness (maturity)
 * - Cell = residual risk (display priority)
 *
 * No fallback defaults. Unknown inputs throw.
 */

export type InherentRisk = "critical" | "high" | "medium" | "low";
export type MaturityLevel = "nonexistent" | "initial" | "developing" | "controlled" | "optimized";
export type ResidualRisk = "critical" | "high" | "medium" | "low";

const RISK_MATRIX: Record<InherentRisk, Record<MaturityLevel, ResidualRisk>> = {
  critical: {
    nonexistent: "critical",
    initial:     "high",
    developing:  "high",
    controlled:  "medium",
    optimized:   "low",
  },
  high: {
    nonexistent: "high",
    initial:     "high",
    developing:  "high",
    controlled:  "medium",
    optimized:   "low",
  },
  medium: {
    nonexistent: "high",
    initial:     "medium",
    developing:  "medium",
    controlled:  "low",
    optimized:   "low",
  },
  low: {
    nonexistent: "medium",
    initial:     "medium",
    developing:  "low",
    controlled:  "low",
    optimized:   "low",
  },
};

const VALID_WEIGHTS = new Set<string>(["critical", "high", "medium", "low"]);

/** Normalize raw weight string to InherentRisk or throw */
export function parseWeight(raw: string | null | undefined): InherentRisk {
  const v = (raw ?? "").toLowerCase().trim();
  if (!VALID_WEIGHTS.has(v)) {
    throw new Error(`[riskMatrix] Unknown priority_weight: "${raw}"`);
  }
  return v as InherentRisk;
}

/**
 * Normalize raw maturity string to MaturityLevel.
 *
 * Supports BOTH scoring models:
 * - 5-level maturity (AI/Telecom): Nonexistent → Optimized
 * - 3-level risk (HRV): High Risk → Low Risk
 *
 * HRV risk labels are mapped to equivalent maturity levels internally:
 *   High Risk   → nonexistent  (worst state)
 *   Moderate Risk → developing
 *   Low Risk    → optimized    (best state)
 *
 * UI labels are NOT affected — this is internal normalization only.
 */
export function parseMaturity(raw: string | null | undefined): MaturityLevel {
  const v = (raw ?? "").toLowerCase().trim();

  // Empty / nonexistent
  if (v === "" || v === "nonexistent" || v === "none" || v === "not implemented") return "nonexistent";

  // --- HRV 3-level risk scale ---
  if (v === "high risk" || v === "high_risk") return "nonexistent";
  if (v === "moderate risk" || v === "moderate_risk") return "developing";
  if (v === "low risk" || v === "low_risk") return "optimized";

  // --- Standard 5-level maturity scale ---
  if (v.includes("optimized")) return "optimized";
  // "Defined/Managed", "Defined", "Managed" all map to controlled
  if (v === "controlled" || v.includes("managed") || v.includes("defined")) return "controlled";
  if (v.includes("developing")) return "developing";
  if (v.includes("initial")) return "initial";

  throw new Error(`[riskMatrix] Unknown maturity: "${raw}"`);
}

/**
 * Look up residual risk from the authoritative matrix.
 * Throws on unknown inputs — no silent fallbacks.
 */
export function lookupResidualRisk(
  rawWeight: string | null | undefined,
  rawMaturity: string | null | undefined,
): ResidualRisk {
  const weight = parseWeight(rawWeight);
  const maturity = parseMaturity(rawMaturity);
  return RISK_MATRIX[weight][maturity];
}
